/**
 * Órdenes de compra (spec 21; documento de la dueña del 26-10-2026 y respuestas del 27-10). Un pedido de cotización al
 * proveedor con productos y cantidades, sin precios. Propone lo que está bajo el mínimo de cada tienda y depósito; se
 * imprime, se guarda en PDF o se envía por correo al proveedor; y se pasa a compras que se editan antes de confirmar. El
 * proveedor puede entregar por partes (aclaración de la dueña del 27-10): cada entrega es una compra que viene de la orden,
 * y la orden lleva lo recibido y lo pendiente de cada producto. No mueve inventario, ni cuentas por pagar, ni libros.
 */
import { z } from 'zod';
import { decimalPositivo, paginacion, uuid } from './comun.ts';

export const ESTADOS_ORDEN_COMPRA = ['abierta', 'parcial', 'completa', 'cerrada', 'anulada'] as const;
export type EstadoOrdenCompra = (typeof ESTADOS_ORDEN_COMPRA)[number];
export const ESTADO_ORDEN_COMPRA_ETIQUETA: Record<EstadoOrdenCompra, string> = {
  abierta: 'Abierta',
  parcial: 'Recibida en parte',
  completa: 'Recibida completa',
  cerrada: 'Cerrada con pendiente',
  anulada: 'Anulada',
};
/** Se le puede recibir otra entrega (pasar a compra): abierta o recibida en parte. */
export const ORDEN_RECIBE: readonly EstadoOrdenCompra[] = ['abierta', 'parcial'];

const lineaOrdenInput = z.object({
  producto_id: uuid,
  cantidad: decimalPositivo,
  nota: z.string().trim().max(200).optional(),
});

export const ordenCompraInput = z
  .object({
    proveedor_id: uuid,
    /** Dónde debe llegar (tienda o depósito de la sucursal); la compra lo propone como almacén de ingreso. */
    almacen_id: uuid.nullable().optional(),
    observaciones: z.string().trim().max(500).optional(),
    lineas: z.array(lineaOrdenInput).min(1, 'Agrega al menos un producto').max(200),
  })
  .refine((o) => new Set(o.lineas.map((l) => l.producto_id)).size === o.lineas.length, { message: 'Hay productos repetidos: deja uno con la cantidad total', path: ['lineas'] });
export type OrdenCompraInput = z.infer<typeof ordenCompraInput>;

export const ordenCompraAnularInput = z.object({ motivo: z.string().trim().min(5, 'Indica el motivo (mínimo 5 caracteres)').max(300) });

export const ordenesCompraFiltros = paginacion.extend({
  estado: z.enum(ESTADOS_ORDEN_COMPRA).optional(),
  q: z.string().trim().max(80).optional(),
});
export type OrdenesCompraFiltros = z.infer<typeof ordenesCompraFiltros>;

/** Sugeridos por el mínimo: se puede excluir la orden que se está editando (sus cantidades no cuentan como ya pedidas). */
export const sugeridosCompraQuery = z.object({
  q: z.string().trim().max(80).optional(),
  excluir_orden_id: uuid.optional(),
});
export type SugeridosCompraQuery = z.infer<typeof sugeridosCompraQuery>;

export interface OrdenCompraLineaDTO {
  id: string;
  orden: number;
  producto_id: string;
  producto_codigo: string;
  producto_nombre: string;
  unidad: string;
  /** Pedida. */
  cantidad: string;
  /** En las compras no anuladas que vienen de la orden. */
  recibido: string;
  /** Pedida − recibida, nunca menor que cero (si llegó de más, 0). */
  pendiente: string;
  nota: string | null;
}

/** Una entrega: una compra que viene de la orden (también las anuladas, que no cuentan como recibido). */
export interface OrdenCompraEntregaDTO {
  compra_id: string;
  /** N° interno de la compra (null en borrador). */
  numero_formateado: string | null;
  /** N° de la factura o, en una nota de entrega, de la nota (spec 10, enmienda 2026-10-29). */
  numero_factura: string;
  fecha_factura: string;
  estado: 'borrador' | 'confirmada' | 'anulada';
  /** Entrega recibida con factura o con nota de entrega (sin factura). */
  tipo_documento: 'factura' | 'nota_entrega';
}

export interface OrdenCompraResumenDTO {
  id: string;
  numero_formateado: string;
  proveedor_razon_social: string;
  proveedor_rif: string;
  almacen_nombre: string | null;
  estado: EstadoOrdenCompra;
  lineas: number;
  creado_en: string;
  creado_por_nombre: string;
  /** Compras no anuladas que vienen de la orden (entregas recibidas). */
  entregas: number;
  /** Última vez que se envió por correo y a quién (de la auditoría). */
  enviada_en: string | null;
}

export interface OrdenCompraDTO extends Omit<OrdenCompraResumenDTO, 'lineas'> {
  sucursal_id: string;
  sucursal_nombre: string;
  proveedor_id: string;
  proveedor_direccion_fiscal: string;
  proveedor_telefono: string | null;
  proveedor_correo: string | null;
  almacen_id: string | null;
  observaciones: string | null;
  impresiones: number;
  enviada_a: string | null;
  /** Quién la anuló o la cerró, cuándo y por qué. */
  anulada_por_nombre: string | null;
  anulada_en: string | null;
  motivo_anulacion: string | null;
  lineas: OrdenCompraLineaDTO[];
  compras: OrdenCompraEntregaDTO[];
}

/** Dónde falta: el mínimo de la sucursal (todas sus tiendas y depósitos) o el de una tienda o depósito. */
export interface FaltanteMinimoDTO {
  almacen_id: string | null;
  /** «Sucursal Barquisimeto» o el nombre de la tienda o el depósito. */
  ubicacion: string;
  disponible: string;
  minimo: string;
  falta: string;
}

/**
 * Un producto bajo el mínimo. `sugerido` = lo que falta (el mayor entre lo que falta a la sucursal y la suma de lo que
 * falta a cada tienda y depósito) menos lo que sigue pendiente de recibir en otras órdenes abiertas o recibidas en parte.
 */
export interface SugeridoCompraDTO {
  producto_id: string;
  producto_codigo: string;
  producto_nombre: string;
  unidad: string;
  falta: string;
  en_ordenes: string;
  sugerido: string;
  detalle: FaltanteMinimoDTO[];
}
